import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from '../primitives/Icon';

export interface CrumbLink {
  readonly label: string;
  readonly to?: string | undefined;
}

/** Breadcrumb trail. The final entry is the current page and is not a link. */
export function Crumb({ items }: { readonly items: readonly CrumbLink[] }): JSX.Element {
  return (
    <nav className="crumb" aria-label="Breadcrumb">
      {items.map((item, i) => (
        <span
          key={`${item.label}-${i}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {i > 0 && <Icon name="chevR" />}
          {item.to === undefined ? <b>{item.label}</b> : <Link to={item.to}>{item.label}</Link>}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({
  crumbs,
  title,
  intro,
  actions,
  children,
}: {
  readonly crumbs?: readonly CrumbLink[] | undefined;
  readonly title: string;
  readonly intro?: ReactNode | undefined;
  readonly actions?: ReactNode | undefined;
  /** Extra content under the intro, e.g. a chip row. */
  readonly children?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="page-hd">
      <div>
        {crumbs !== undefined && <Crumb items={crumbs} />}
        <h1>{title}</h1>
        {intro !== undefined && (
          <p className="muted" style={{ maxWidth: 820 }}>
            {intro}
          </p>
        )}
        {children}
      </div>
      {actions}
    </div>
  );
}

/** Fixed classification watermark. Present on every screen by design. */
export function Watermark(): JSX.Element {
  return <div className="watermark">Internal · illustrative data</div>;
}
